<template>
  <!-- The grid's shape while the building loads, so nothing moves when the photos land -->
  <div v-if="loading" class="photos" aria-busy="true">
    <Skeleton v-for="cell in gridCells" :key="cell" class="photo" height="100%" />
  </div>

  <!-- Nothing uploaded yet: the one place on the page to start the gallery -->
  <div v-else-if="!images.length" class="empty">
    <span class="tile"><Icon name="image-plus" /></span>
    <div class="copy">
      <Text variant="h4">Add building photos</Text>
      <Text variant="body-medium" color="secondary">
        The first photo is the cover for every unit that has none of its own · up to 25 MB each
      </Text>
    </div>
    <Button size="medium" data-manage-action data-testid="add-photos" @click="emit('edit')">Add photos</Button>
  </div>

  <div v-else class="photos" :class="`count-${Math.min(images.length, gridCells)}`" data-testid="building-photos">
    <button
      v-for="(image, index) in shown"
      :key="image.id"
      type="button"
      class="photo"
      :aria-label="index === 0 ? 'Cover photo' : `Photo ${index + 1}`"
      @click="viewerRef?.show(index)"
    >
      <img :src="image.thumbnailUrl || image.url" :alt="image.fileName" loading="lazy" />
      <Badge
        v-if="index === 0"
        label="Cover photo"
        variant="ghost"
        size="small"
        class="cover-badge"
        aria-hidden="true"
      />
    </button>

    <div class="actions">
      <Button
        v-if="images.length > 1"
        variant="secondary"
        size="small"
        data-testid="show-all-photos"
        @click="viewerRef?.show(0)"
      >
        <Icon name="grid-01" size="small" />
        <span>{{ `Show all ${images.length} photos` }}</span>
      </Button>
      <Button variant="secondary" size="small" data-manage-action data-testid="edit-photos" @click="emit('edit')">
        <Icon name="pencil" size="small" />
        <span>Edit photos</span>
      </Button>
    </div>
  </div>

  <FileViewerModal ref="viewerRef" :files="images" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Badge, Button, Icon, Skeleton, Text } from '@tallo/design-system';
import { FileDto, FileViewerModal } from '@tallo/file-manager';

/** The building's gallery at the top of its details: the cover large, the next four beside it */
const props = withDefaults(defineProps<{ images: FileDto[]; loading?: boolean }>(), { loading: false });
const emit = defineEmits<{ edit: [] }>();

/** The grid has five cells; the rest of the gallery is in the viewer */
const gridCells = 5;

const viewerRef = ref<InstanceType<typeof FileViewerModal>>();

const shown = computed(() => props.images.slice(0, gridCells));
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 12rem;
  padding: var(--t-spacing-md);
  text-align: center;
  background: var(--t-color-neutral-white);
  border: 1px dashed var(--t-color-neutral-600);
  border-radius: var(--t-border-radius-lg);

  .tile {
    display: grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: var(--t-border-radius-full-rounded);
    background: var(--t-color-neutral-100);
  }

  .copy {
    display: flex;
    flex-direction: column;
    gap: var(--t-spacing-xs);
  }
}

// The cover takes the tall left cell; fewer photos take fewer cells rather than leaving holes
.photos {
  position: relative;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0.5rem;
  height: 20rem;
  border-radius: var(--t-border-radius-lg);
  overflow: hidden;

  &.count-1 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }

  &.count-2 {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr;
  }

  &.count-3 {
    grid-template-columns: 2fr 1fr;
  }

  // With four, the second photo stands tall beside the cover and the last two stack
  &.count-4 .photo:nth-child(2) {
    grid-row: 1 / -1;
  }

  // The cover on its own row, the rest two to a row; said per count to outrank the rules above
  @include mobile {
    &,
    &.count-2,
    &.count-3,
    &.count-4 {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;
      height: auto;
    }

    &.count-4 .photo:nth-child(2) {
      grid-row: auto;
    }
  }
}

.photo {
  position: relative;
  display: block;
  min-width: 0;
  min-height: 0;

  &:first-child {
    grid-row: 1 / -1;

    @include mobile {
      grid-column: 1 / -1;
      grid-row: auto;
      aspect-ratio: 16 / 9;
    }
  }

  @include mobile {
    aspect-ratio: 1;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// The resets are the button's; a loading cell keeps the skeleton's own look
button.photo {
  padding: 0;
  border: none;
  background: var(--t-color-neutral-100);
  cursor: pointer;
}

// On the cover itself, so it stays there whichever layout the actions take. Doubled to outrank the badge's own background
.cover-badge.cover-badge {
  position: absolute;
  left: var(--t-spacing-md);
  bottom: var(--t-spacing-md);
  background: rgb(255 255 255 / 90%);
  pointer-events: none;
}

.actions {
  position: absolute;
  right: var(--t-spacing-md);
  bottom: var(--t-spacing-md);
  display: flex;
  gap: var(--t-spacing-sm);

  @include mobile {
    position: static;
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}
</style>
